// Модал «Магия вариантов»: человек говорит, откуда, куда и чего хочет, —
// сервер отдаёт эти пожелания вместе со всей библиотекой выбранной модели и
// возвращает готовые варианты. Варианты появляются на доске только после
// подтверждения: как и у распознавания, ничего не сохраняется молча.
import { useEffect, useMemo, useRef, useState } from 'react';
import type { TripVariant } from '../../types';
import { MAX_VARIANTS } from '../../types';
import { computeVariantTotals, formatMoney, segmentSubtitle, segmentTitle } from '../../core';
import { useActions, useStore } from '../../store';
import { useAuth } from '../../auth';
import type { ApiError } from '../../auth';
import { useI18n, useT } from '../../i18n';
import type { Translate } from '../../i18n';
import { Modal } from '../shell/Modal';
import { fetchMagicConfig, magicPlan } from './api';
import type { MagicConfig, MagicVariant } from './api';
import { toMagicSegments } from './payload';
import { applyMagicVariants } from './apply';
import styles from './MagicModal.module.css';

export interface MagicModalProps {
  open: boolean;
  onClose(): void;
}

/** Коды ошибок сервера, у которых есть свой текст; остальное — magic.err.unknown. */
const KNOWN_ERROR_CODES = new Set([
  'unauthorized',
  'disabled',
  'no_model',
  'no_key',
  'bad_request',
  'too_large',
  'rate_limited',
  'timeout',
  'provider',
  'bad_response',
  'unknown',
]);

function errorMessage(t: Translate, err: ApiError): string {
  const code = KNOWN_ERROR_CODES.has(err.error) ? err.error : 'unknown';
  const params = code === 'provider' || code === 'bad_request' ? { message: err.message ?? '' } : undefined;
  return t(`magic.err.${code}`, params);
}

interface ResultState {
  model: string;
  variants: MagicVariant[];
  notes: string[];
}

const DEFAULT_STAY_MIN = 5;
const DEFAULT_STAY_MAX = 10;

export function MagicModal({ open, onClose }: MagicModalProps) {
  const state = useStore();
  const actions = useActions();
  const auth = useAuth();
  const t = useT();
  const { lang } = useI18n();

  const [config, setConfig] = useState<MagicConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  // 401 у гостя — не ошибка, а другой текст баннера: магия требует входа.
  const [isGuest, setIsGuest] = useState(false);
  // Всё остальное, что помешало узнать состояние магии, — именно ошибка, и
  // выдавать её за «выключено администратором» нельзя: это разные поводы.
  const [configError, setConfigError] = useState<string | null>(null);

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [roundTrip, setRoundTrip] = useState(true);
  const [stayMin, setStayMin] = useState(DEFAULT_STAY_MIN);
  const [stayMax, setStayMax] = useState(DEFAULT_STAY_MAX);
  const [wishes, setWishes] = useState('');
  const [count, setCount] = useState(2);

  const [loading, setLoading] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const [result, setResult] = useState<ResultState | null>(null);
  const [picked, setPicked] = useState<boolean[]>([]);

  const segments = useMemo(() => toMagicSegments(state, t), [state, t]);
  const flights = useMemo(() => segments.filter((s) => s.kind === 'flight'), [segments]);

  // Свободные места на доске: магия добавляет варианты, а не заменяет их.
  // auth.maxVariants уже учитывает персональный лимит из админки.
  const room = Math.max(0, auth.maxVariants - state.variants.length);
  const maxCount = Math.max(1, Math.min(config?.maxVariants ?? MAX_VARIANTS, room || 1));

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setConfigLoading(true);
    setIsGuest(false);
    setConfig(null);
    setConfigError(null);
    fetchMagicConfig().then((res) => {
      if (cancelled) return;
      if (res.ok) setConfig(res.value);
      else if (res.error.error === 'unauthorized') setIsGuest(true);
      else setConfigError(errorMessage(t, res.error));
      setConfigLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // Перевод в зависимостях не нужен: конфиг тянется один раз на открытие, а
    // смена языка посреди открытого модала текст уже полученной ошибки не меняет.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Подсказка вместо пустых полей: маршрут первого рейса библиотеки почти
  // всегда и есть тот, ради которого магию открыли. Заполняем один раз при
  // открытии — дальше поля принадлежат человеку.
  useEffect(() => {
    if (!open) return;
    const first = flights[0];
    if (!first) return;
    setFrom((v) => v || first.from || '');
    setTo((v) => v || first.to || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Число вариантов не должно оказаться больше, чем влезет на доску.
  useEffect(() => {
    setCount((c) => Math.min(Math.max(1, c), maxCount));
  }, [maxCount]);

  useEffect(() => {
    if (!open) abortRef.current?.abort();
    return () => abortRef.current?.abort();
  }, [open]);

  if (!open) return null;

  function handleClose() {
    abortRef.current?.abort();
    setLoading(false);
    setRunError(null);
    setResult(null);
    setPicked([]);
    onClose();
  }

  const ready = Boolean(config?.ready);
  const overLimit = Boolean(config?.maxSegments && segments.length > config.maxSegments);
  const canRun = ready && flights.length > 0 && from.trim() !== '' && to.trim() !== '' && room > 0;

  async function handleRun() {
    if (loading || !canRun) return;
    setRunError(null);
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    const res = await magicPlan(
      {
        from: from.trim(),
        to: to.trim(),
        lang,
        roundTrip,
        stayMinDays: roundTrip ? stayMin : null,
        stayMaxDays: roundTrip ? stayMax : null,
        maxVariants: count,
        wishes: wishes.trim(),
        travelers: state.travelers,
        currency: state.displayCurrency,
        segments,
      },
      controller.signal,
    );
    // Отмену просил сам человек — показывать ему после этого ошибку сети
    // значит отвечать не на то действие, которое он совершил.
    if (controller.signal.aborted) return;
    setLoading(false);
    if (res.ok) {
      setResult({ model: res.value.model, variants: res.value.variants, notes: res.value.notes });
      // Отмечены сразу все, что помещаются на доску: снимать галочки с того,
      // что всё равно не влезет, — работа за пользователя.
      setPicked(res.value.variants.map((_, i) => i < room));
    } else {
      setRunError(errorMessage(t, res.error));
    }
  }

  function handleCancelRun() {
    abortRef.current?.abort();
    setLoading(false);
  }

  function handleSave() {
    if (!result) return;
    const picks = result.variants.filter((_, i) => picked[i]);
    if (picks.length === 0) return;
    actions.replaceState(applyMagicVariants(state, picks, auth.maxVariants, t));
    handleClose();
  }

  const selectedCount = picked.filter(Boolean).length;
  // Больше свободных мест на доске принять нельзя — лишние галочки не обманут.
  const overflow = Math.max(0, selectedCount - room);

  const footer = result ? (
    <>
      <button type="button" className="btn" onClick={() => setResult(null)}>
        {t('magic.back')}
      </button>
      <button
        type="button"
        className="btn btn-primary"
        disabled={selectedCount === 0 || overflow > 0}
        onClick={handleSave}
      >
        {t('magic.add', { count: selectedCount })}
      </button>
    </>
  ) : (
    <>
      <button type="button" className="btn" onClick={loading ? handleCancelRun : handleClose}>
        {t('form.cancel')}
      </button>
      <button
        type="button"
        className="btn btn-primary"
        disabled={loading || !canRun}
        aria-busy={loading || undefined}
        onClick={() => void handleRun()}
      >
        {loading ? t('magic.running') : t('magic.run')}
      </button>
    </>
  );

  return (
    <Modal title={t('magic.title')} onClose={handleClose} footer={footer} wide>
      {result ? (
        <ResultStep
          result={result}
          picked={picked}
          overflow={overflow}
          onToggle={(i) => setPicked((prev) => prev.map((v, idx) => (idx === i ? !v : v)))}
        />
      ) : (
        <div className={styles.form}>
          <div className={styles.row}>
            <div className={styles.field}>
              <label htmlFor="magic-from" className="field-label">
                {t('magic.from')}
              </label>
              <input
                id="magic-from"
                className="input"
                value={from}
                autoFocus
                placeholder={t('magic.from.placeholder')}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="magic-to" className="field-label">
                {t('magic.to')}
              </label>
              <input
                id="magic-to"
                className="input"
                value={to}
                placeholder={t('magic.to.placeholder')}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
          </div>

          <label className={styles.switchRow}>
            <input type="checkbox" checked={roundTrip} onChange={(e) => setRoundTrip(e.target.checked)} />
            <span>{t('magic.roundTrip')}</span>
          </label>

          {roundTrip && (
            <div className={styles.stayBlock}>
              <span className="field-label">{t('magic.stay')}</span>
              <div className={styles.stayRow}>
                <label className={styles.stayField}>
                  <span className={styles.stayLabel}>{t('magic.stay.from')}</span>
                  <input
                    className={`input ${styles.num}`}
                    type="number"
                    min={0}
                    max={365}
                    value={stayMin}
                    onChange={(e) => setStayMin(Math.max(0, Math.round(e.target.valueAsNumber || 0)))}
                  />
                </label>
                <label className={styles.stayField}>
                  <span className={styles.stayLabel}>{t('magic.stay.to')}</span>
                  <input
                    className={`input ${styles.num}`}
                    type="number"
                    min={0}
                    max={365}
                    value={stayMax}
                    onChange={(e) => setStayMax(Math.max(0, Math.round(e.target.valueAsNumber || 0)))}
                  />
                </label>
              </div>
              <p className={styles.hint}>{t('magic.stay.hint')}</p>
            </div>
          )}

          <div className={styles.field}>
            <label htmlFor="magic-wishes" className="field-label">
              {t('magic.wishes')}
            </label>
            <textarea
              id="magic-wishes"
              className={`textarea ${styles.textarea}`}
              rows={4}
              value={wishes}
              maxLength={config?.maxWishesChars ?? 1000}
              placeholder={t('magic.wishes.placeholder')}
              onChange={(e) => setWishes(e.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="magic-count" className="field-label">
              {t('magic.count')}
            </label>
            <input
              id="magic-count"
              className={`input ${styles.num}`}
              type="number"
              min={1}
              max={maxCount}
              value={count}
              onChange={(e) => setCount(Math.min(maxCount, Math.max(1, Math.round(e.target.valueAsNumber || 1))))}
            />
            <p className={styles.hint}>{t('magic.count.hint', { count: room })}</p>
          </div>

          {room === 0 && <p className={styles.warnBanner}>{t('magic.noRoom')}</p>}

          {/* Предел библиотеки зависит от контекста выбранной модели, поэтому
              приезжает с сервера вместе с конфигом. Лишнее не ошибка: сервер
              отрежет самое далёкое от маршрута — здесь об этом предупреждаем
              заранее, чтобы результат не выглядел неполным без объяснения. */}
          {flights.length === 0 ? (
            <p className={styles.warnBanner}>{t('magic.library.empty')}</p>
          ) : overLimit ? (
            <p className={styles.warnBanner}>
              {t('magic.library.trimmed', { count: segments.length, limit: config?.maxSegments ?? 0 })}
            </p>
          ) : (
            <p className={styles.hint}>{t('magic.library', { count: segments.length })}</p>
          )}

          {!configLoading && !ready && !configError && (
            <p className={styles.offBanner}>{t(isGuest ? 'magic.off.guest' : 'magic.off')}</p>
          )}

          {configError && (
            <p className={styles.errorBanner} role="alert">
              {configError}
            </p>
          )}

          <p className={styles.hint}>{t('magic.privacy')}</p>

          {runError && (
            <p className={styles.errorBanner} role="alert">
              {runError}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

interface ResultStepProps {
  result: ResultState;
  picked: boolean[];
  overflow: number;
  onToggle(index: number): void;
}

/** Второй шаг: карточки вариантов с реальным итогом, посчитанным на клиенте. */
function ResultStep({ result, picked, overflow, onToggle }: ResultStepProps) {
  const state = useStore();
  const t = useT();

  return (
    <div className={styles.result}>
      <p className={styles.caption}>{t('magic.review')}</p>
      <p className={styles.modelLine}>{t('magic.model', { model: result.model })}</p>

      {result.notes.length > 0 && (
        <div className={styles.notesBlock}>
          <p className={styles.notesTitle}>{t('magic.notes')}</p>
          <ul className={styles.notes}>
            {result.notes.map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        </div>
      )}

      {overflow > 0 && <p className={styles.warnBanner}>{t('magic.tooMany')}</p>}

      {result.variants.length === 0 ? (
        <p className={styles.hint}>{t('magic.nothing')}</p>
      ) : (
        result.variants.map((variant, i) => {
          // Итог считает то же ядро, что и доска: цифра в карточке обязана
          // совпасть с той, которую человек увидит после добавления.
          const preview: TripVariant = {
            id: `magic-preview-${i}`,
            name: variant.name,
            color: '#5b7c99',
            segmentIds: variant.segmentIds.slice(),
          };
          const totals = computeVariantTotals(preview, state, t);
          return (
            <section key={i} className={styles.card}>
              <div className={styles.cardHead}>
                <label className={styles.include}>
                  <input type="checkbox" checked={picked[i] ?? false} onChange={() => onToggle(i)} />
                  <span className={styles.cardName}>{variant.name}</span>
                </label>
                <span className={`mono ${styles.total}`}>
                  {formatMoney(totals.totalWithBaggageMinor, state.displayCurrency, state.currencies)}
                </span>
              </div>

              {variant.summary && <p className={styles.summary}>{variant.summary}</p>}

              <ul className={styles.segments}>
                {variant.segmentIds.map((id) => {
                  const seg = state.library[id];
                  if (!seg) return null;
                  return (
                    <li key={id} className={styles.segment}>
                      <span className={styles.segTitle}>{segmentTitle(seg, t)}</span>
                      <span className={styles.segSub}>{segmentSubtitle(seg, state.currencies, t)}</span>
                    </li>
                  );
                })}
              </ul>

              <p className={styles.meta}>
                {variant.stayDays !== null && (
                  <span className={styles.metaItem}>{t('magic.stayDays', { days: variant.stayDays })}</span>
                )}
                <span className={styles.metaItem}>{t('magic.segments', { count: variant.segmentIds.length })}</span>
              </p>

              {(variant.notes.length > 0 || totals.warnings.length > 0) && (
                <ul className={styles.cardNotes}>
                  {variant.notes.map((n, idx) => (
                    <li key={`n${idx}`}>{n}</li>
                  ))}
                  {totals.warnings.map((w, idx) => (
                    <li key={`w${idx}`} className={w.level === 'error' ? styles.noteError : styles.noteWarn}>
                      {w.message}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })
      )}
    </div>
  );
}
